import { stringToSyslogLevel, LogModule, LogComponent } from './loggerTypes'
import type { LoggerClientConfig } from './loggerTypes'

/**
 * Helper to resolve ${VARNAME} against provided envMap
 */
function resolveBinding(value: unknown, envMap: Record<string, string>): unknown {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  const match = /^\$\{(\w+)\}$/.exec(trimmed)
  if (!match) return trimmed
  
  return envMap[match[1]] ?? ''
}

export function parseLoggerConfig(rawConfig: unknown, envMap: Record<string, string> = {}): LoggerClientConfig {
  if (!rawConfig || typeof rawConfig !== 'object') {
    throw new Error('Logger SDK configuration is missing or invalid')
  }

  const config = rawConfig as Record<string, unknown>

  const moduleVal = resolveBinding(config.module, envMap)
  const componentVal = resolveBinding(config.component, envMap)
  const logLevelVal = resolveBinding(config.logLevel, envMap)

  if (moduleVal == null || moduleVal === '') throw new Error('Logger config must include "module"')
  if (componentVal == null || componentVal === '') throw new Error('Logger config must include "component"')
  if (logLevelVal == null || logLevelVal === '') throw new Error('Logger config must include "logLevel"')

  const result: LoggerClientConfig = {
    module: typeof moduleVal === 'string'
      ? (LogModule as any)[moduleVal] ?? moduleVal
      : moduleVal as (number | string),
    component: typeof componentVal === 'string'
      ? (LogComponent as any)[componentVal] ?? componentVal
      : componentVal as (number | string),
    logLevel: stringToSyslogLevel(String(logLevelVal)),
    commonInfoKeys: Array.isArray(config.commonInfoKeys)
      ? config.commonInfoKeys.map(String)
      : [],
    consoleStructured: config.consoleStructured === true,
  }

  if (config.otel && typeof config.otel === 'object') {
    const otelConfig = config.otel as Record<string, unknown>
    const enabledRaw = resolveBinding(otelConfig.enabled, envMap)
    
    result.otel = {
      enabled: enabledRaw === 'true' || enabledRaw === true,
      serviceName: String(resolveBinding(otelConfig.serviceName, envMap) || 'unknown'),
      serviceVersion: String(resolveBinding(otelConfig.serviceVersion, envMap) || 'unknown'),
      environment: String(resolveBinding(otelConfig.environment, envMap) || 'unknown'),
      collectorUrl: String(resolveBinding(otelConfig.collectorUrl, envMap) || ''),
      headers: otelConfig.headers as Record<string, string> | undefined,
      propagators: Array.isArray(otelConfig.propagators) ? otelConfig.propagators.map(String) : undefined,
    }

    if (otelConfig.batch && typeof otelConfig.batch === 'object') {
      const batchConfig = otelConfig.batch as Record<string, unknown>
      result.otel.batch = {
        scheduledDelayMillis: Number(batchConfig.scheduledDelayMillis) || undefined,
        maxExportBatchSize: Number(batchConfig.maxExportBatchSize) || undefined,
        maxQueueSize: Number(batchConfig.maxQueueSize) || undefined,
        exportTimeoutMillis: Number(batchConfig.exportTimeoutMillis) || undefined,
      }
    }
  }

  return result
}
